import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription, pipe, zip, Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';

import { ILocation } from '../../../location/interfaces/location.interface';
import { ILot } from '../../../lot';
import {
  LocalStorageKeys,
  OrderReturnApiService,
  OptionInterface,
  CheckedItemInterface,
  LocationApiService,
  SnackBarConfig,
  LotApiService,
  UnitOfMeasureApiService,
} from '../../../shared';
import {
  IUnitOfMeasure,
  IUnitConversionRule,
} from '../../../unit-of-measure/interfaces/unit-of-measure.interface';
import {
  OrderReturnInterface,
  OrderReturnItemInterface,
  OrderReturnFormInterface,
  OrderReturnItemFormInterface,
  OrderReturnCustomItems,
} from '../../interfaces/order-return.interface';
import {
  OrderSalesInterface,
  OrderSalesItemInterface,
} from '../../interfaces/order-sales.interface';
import { EItemType } from '../../interfaces/order.interface';

@Component({
  selector: 'lum-order-return-form',
  templateUrl: './order-return-form.component.html',
  styleUrls: ['./order-return-form.component.scss'],
})
export class OrderReturnFormComponent implements OnInit, OnDestroy {
  parentSalesOrderId :number = 0;
  orderReturnId :number = 0;

  currentParentSalesOrder$ :Observable<OrderSalesInterface>;
  currentParentSalesOrderItems$ :Observable<OrderSalesItemInterface[]>;
  currentOrderReturn$ :Observable<OrderReturnInterface>;
  currentOrderReturnItems$ :Observable<OrderReturnItemInterface[]>;
  loading$ :Observable<boolean>;

  currentParentSalesOrder :OrderSalesInterface;
  currentParentSalesOrderItems :OrderSalesItemInterface[] = [];
  currentOrderReturn :OrderReturnInterface;
  currentOrderReturnItems :OrderReturnItemInterface[] = [];

  loadingLocations$ :Observable<boolean>;
  loadingLots$ :Observable<boolean>;
  loadingUnitOfMeasures$ :Observable<boolean>;

  locationsByRfqId :{ [key :string] :ILocation[] };
  locationsByRfqId$ :Observable<{ [key :string] :ILocation[] }>;
  lotsByRfqId :{ [rfqId :string] :{ [locationId :string] :ILot[] } };
  lotsByRfqId$ :Observable<{
    [rfqId :string] :{ [locationId :string] :ILot[] };
  }>;
  unitOfMeasuresByRfqId :{ [key :string] :IUnitOfMeasure[] };
  unitOfMeasuresByRfqId$ :Observable<{
    [key :string] :IUnitOfMeasure[];
  }>;

  currentRfqId :number;
  currentLocationId :number;
  currentUnitClassId :number;

  submittingForm$ :Observable<boolean>;

  form :UntypedFormGroup;

  eItemType = EItemType;

  private onDestroy$ = new Subject();

  constructor(
    private orderReturnService :OrderReturnApiService,
    private route :ActivatedRoute,
    private router :Router,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private locationApiService :LocationApiService,
    private lotApiService :LotApiService,
    private unitOfMeasureApiService :UnitOfMeasureApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.getRouterParams().subscribe();

    if (this.orderReturnId) {
      this.getParentSalesOrderAndOrderReturn().subscribe();
    } else {
      this.getParentSalesOrder().subscribe();
    }
    this.getLocationsById().subscribe();
    this.getLotsById().subscribe();
    this.getUnitOfMeasuresById().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onChangeQuantityEvent(index :number) :void {
    this.setValidationsLotAndLocation(index);
  }

  onCheckChangeReStock(checkbox :CheckedItemInterface) :void {
    this.form?.get('restock')?.setValue(checkbox.checked);

    this.items?.controls?.forEach((item :AbstractControl, index :number) => {
      this.rowResetLotAndLocation(item);
      this.setValidationsLotAndLocation(index);
    });
  }

  onSubmit() :void {
    this.form.markAllAsTouched();

    try {
      if (
        this.form.value.orderReturnDate === null ||
        this.form.value.orderReturnDate === ''
      ) {
        throw new Error('Return order date is required');
      }

      this.items?.value.forEach(
        (item :OrderReturnItemFormInterface, idx :number) => {
          if (item.quantity > item.availableQuantity) {
            throw new Error('Quantity should not exceed the refundable quantity');
          }

          if (item.quantity < 0) {
            throw new Error('Quantity should not be less than zero');
          }

          if (item.quantity === null) {
            throw new Error('Quantity field is required');
          }

          if (item.itemType === EItemType.PRODUCT && !item.uomId) {
            throw new Error('Unit of measure is required');
          }

          if (this.isRestock && !item.locationId && item.quantity > 0) {
            throw new Error('Location is required');
          }

          if (
            this.isRestock &&
            item.hasLotTracking &&
            !item.lotId &&
            item.quantity > 0
          ) {
            throw new Error('Lot is required');
          }
        },
      );

      const items = this.items.value.filter(
        (row :OrderReturnItemFormInterface) => row.quantity && row.quantity > 0,
      );

      if (items.length <= 0) {
        throw new Error('Return order should include at least one(1) item');
      }

      this.orderReturnService
        .returnOrder({
          id: this.form.value.id,
          salesOrderId: this.parentSalesOrderId,
          orderDate: this.form.value.orderReturnDate,
          restock: this.form.value.restock,
          customRefundAmount: this.form.value.customRefundAmount,
          restockingFee: this.form.value.restockingFee,
          items,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :{ success :string }) => {
            this.snackBar.open(response.success, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.router.navigate(['orders/return-orders', this.parentSalesOrderId]);
          },
          error: (response) => {
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    } catch (error :any) {
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
    }
  }

  onSelectUnitOfMeasureEvent(unitOfMeasure :OptionInterface, index :number) :void {
    const rfqId = this.items.controls[index]?.get('rfqId')?.value;
    const baseUomId = this.items.controls[index]?.get('baseUomId')?.value;
    const orderedQuantity =
      this.items.controls[index]?.get('orderedQuantity')?.value;

    this.items.at(index).patchValue({
      uomId: unitOfMeasure.value,
      selectedUnitOfMeasure: {
        value: unitOfMeasure.value,
        label: unitOfMeasure.label,
        subLabel: '',
      },
      quantity: 0,
    });

    this.unitOfMeasureApiService
      .getSpecificConversionRule({
        params: {
          rfqId: rfqId,
          baseUnitOfMeasureId: baseUomId,
          convertedUnitOfMeasureId: unitOfMeasure.value,
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IUnitConversionRule) => {
          let newavailableQuantity = orderedQuantity;

          if (response.conversionFactor) {
            newavailableQuantity = orderedQuantity / response.conversionFactor;
          }

          this.items.at(index).patchValue({
            availableQuantity: parseFloat(Number(newavailableQuantity).toFixed(2)),
            baseQuantity: response.conversionFactor ? response.conversionFactor : 1,
          });

          this.setValidationsQuantity(index);

          this.cd.detectChanges();
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
    this.cd.detectChanges();
  }

  onSelectLocationEvent(location :OptionInterface, index :number) :void {
    this.items.at(index).patchValue({
      locationId: location.value,
      selectedLocation: {
        value: location.value,
        label: location.label,
        subLabel: '',
      },
      lotId: null,
      selectedLot: {
        value: null,
        label: '',
        subLabel: '',
      },
    });
    this.cd.detectChanges();
  }

  onSelectLotEvent(lot :OptionInterface, index :number) :void {
    this.items.at(index).patchValue({
      lotId: lot.value,
      selectedLot: {
        value: lot.value,
        label: lot.label,
        subLabel: '',
      },
    });
    this.cd.detectChanges();
  }

  onOpenLocationListEvent(index :number) :void {
    const rfqId = this.items.controls[index]?.get('rfqId')?.value;
    this.currentRfqId = rfqId;
    // CHECK IF RFQ ID LOCATIONS ALREADY LOADED
    if (
      !this.locationsByRfqId ||
      !Object.keys(this.locationsByRfqId).length ||
      !this.locationsByRfqId[rfqId]
    ) {
      // FETCH FROM DB
      this.locationApiService.getRfqLocations({
        id: rfqId,
        params: {
          relations: 'rfqstockLocations,parent',
        },
      });
    }
  }

  onOpenLotListEvent(index :number) :void {
    const rfqId = this.items.controls[index]?.get('rfqId')?.value;
    const locationId = this.items.controls[index]?.get('locationId')?.value;
    this.currentRfqId = rfqId;
    this.currentLocationId = locationId;
    // CHECK IF RFQ ID LOTS ALREADY LOADED
    if (
      locationId &&
      (!this.lotsByRfqId ||
        !Object.keys(this.lotsByRfqId).length ||
        !this.lotsByRfqId[rfqId] ||
        !this.lotsByRfqId[rfqId][locationId])
    ) {
      // FETCH FROM DB
      this.lotApiService.getById({
        id: rfqId,
        params: {
          locationId: locationId.toString(),
        },
      });
    }
  }

  onOpenUnitOfMeasureListEvent(index :number) :void {
    const rfqId = this.items.controls[index]?.get('rfqId')?.value;
    const unitClassId = this.items.controls[index]?.get('unitClassId')?.value;
    this.currentRfqId = rfqId;
    this.currentUnitClassId = unitClassId;
    // CHECK IF RFQ ID UNIT OF MEASURES ALREADY LOADED
    if (
      !this.unitOfMeasuresByRfqId ||
      !Object.keys(this.unitOfMeasuresByRfqId).length ||
      !this.unitOfMeasuresByRfqId[rfqId]
    ) {
      // FETCH FROM DB
      this.unitOfMeasureApiService.get({
        id: rfqId,
        params: {
          unitClassId: unitClassId.toString(),
          relations: 'unitConversionRules',
        },
      });
    }
  }

  private createItemFromExistingOrderReturn(
    item :OrderReturnItemInterface,
  ) :UntypedFormGroup {
    const parentSalesOrderItem = this.getMatchedItemFromOrderReturn(item);

    let baseUnitPrice = 0;
    let baseQuantity = 1;
    let totalReturnedBaseQuantity = 0;
    let totalReturnedQuantity = 0;
    let itemType = EItemType.PRODUCT;
    const hasLotTracking = item?.rfq?.lotTracking || false;

    if (item && item.quantity && item.orderedQuantity) {
      if (item.quantity === item.orderedQuantity || item.orderedQuantity === 0) {
        baseUnitPrice = item.unitPrice || 0;
      } else {
        baseQuantity = item.orderedQuantity / item.quantity;
        baseUnitPrice = item.unitPrice / baseQuantity;
      }
    }

    if (
      parentSalesOrderItem &&
      parentSalesOrderItem.totalReturnedBaseQuantity > 0
    ) {
      totalReturnedBaseQuantity = parentSalesOrderItem.totalReturnedBaseQuantity;
    }

    if (totalReturnedBaseQuantity > 0) {
      totalReturnedQuantity = totalReturnedBaseQuantity / baseQuantity;
    }

    const maxBaseQuantity =
      Number(item.orderedQuantity) +
      Number(parentSalesOrderItem?.orderedQuantity) -
      Number(totalReturnedBaseQuantity);
    const maxQuantity =
      Number(item.quantity) +
      Number(parentSalesOrderItem?.quantity) -
      Number(totalReturnedQuantity);

    if (
      (parentSalesOrderItem?.kitId &&
        parentSalesOrderItem.kitId > 0 &&
        parentSalesOrderItem?.rfqId &&
        parentSalesOrderItem.rfqId === 0) ||
      parentSalesOrderItem?.rfq?.type === EItemType.KIT
    ) {
      itemType = EItemType.KIT;
    }

    return this.fb.group({
      id: [item.id, [Validators.required]],
      salesOrderItemId: parentSalesOrderItem?.id,
      productId: item.productId,
      rfqId: item.rfqId,
      kitId: item.kitId,
      sku: item.sku,
      name: item.name,
      productDescription: item.productDescription,
      unitPrice: [item.unitPrice, [Validators.required]],
      baseUnitPrice: baseUnitPrice,
      baseQuantity: baseQuantity,
      quantity: [
        item.quantity,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(~~Number(maxQuantity)),
        ],
      ],
      availableQuantity: parseFloat(Number(maxQuantity).toFixed(2)),
      orderedQuantity: maxBaseQuantity,
      quantityOrdered: parentSalesOrderItem?.orderedQuantity,
      quantityCancelled: parentSalesOrderItem?.quantityCancelled,
      quantityShipped: parentSalesOrderItem?.quantityShipped,
      quantityReturned: parentSalesOrderItem?.totalReturnedBaseQuantity,
      lotId: [
        item.lotId,
        [
          this.initFieldValidationlot(
            this.isRestock,
            hasLotTracking,
            item.quantity,
          ),
        ],
      ],
      selectedLot: {
        value: null,
        label: '',
        subLabel: '',
      },
      locationId: [
        item.locationId,
        [this.initFieldValidationLocation(this.isRestock, item.quantity)],
      ],
      selectedLocation: {
        value: null,
        label: '',
        subLabel: '',
      },
      uomId: [
        itemType === EItemType.PRODUCT ? item?.orderedUomId : null,
        [this.initFieldValidationUom(itemType)],
      ],
      selectedUnitOfMeasure: {
        value: itemType === EItemType.PRODUCT ? item?.orderedUom?.id : null,
        label: itemType === EItemType.PRODUCT ? item?.orderedUom?.name : '',
        subLabel: '',
      },
      baseUomId: item.baseUomId,
      selectedBaseUnitOfMeasure: {
        value: item?.baseUom?.id,
        label: item?.baseUom?.name,
        subLabel: '',
      },
      unitClassId: item?.rfq?.unitClassId || 0,
      itemType: itemType,
      hasLotTracking: hasLotTracking,
    });
  }

  private createItemFromParentSalesOrder(
    item :OrderSalesItemInterface,
  ) :UntypedFormGroup {
    let baseUnitPrice = 0;
    let baseQuantity = 1;
    let totalReturnedBaseQuantity = 0;
    let totalReturnedQuantity = 0;
    let itemType = EItemType.PRODUCT;
    const hasLotTracking = item?.rfq?.lotTracking || false;

    if (item && item.quantity && item.orderedQuantity) {
      if (item.quantity === item.orderedQuantity || item.orderedQuantity === 0) {
        baseUnitPrice = item.unitPrice || 0;
      } else {
        baseQuantity = item.orderedQuantity / item.quantity;
        baseUnitPrice = item.unitPrice / baseQuantity;
      }
    }

    if (item && item.totalReturnedBaseQuantity > 0) {
      totalReturnedBaseQuantity = item.totalReturnedBaseQuantity;
    }

    if (totalReturnedBaseQuantity > 0) {
      totalReturnedQuantity = totalReturnedBaseQuantity / baseQuantity;
    }

    const maxBaseQuantity =
      Number(item.orderedQuantity) - Number(totalReturnedBaseQuantity);
    const maxQuantity = Number(item?.quantity) - Number(totalReturnedQuantity);

    if (
      (item?.kitId && item.kitId > 0 && item?.rfqId && item.rfqId === 0) ||
      item?.rfq?.type === EItemType.KIT
    ) {
      itemType = EItemType.KIT;
    }

    return this.fb.group({
      id: null,
      salesOrderItemId: item.id,
      productId: item.productId,
      rfqId: item.rfqId,
      kitId: item.kitId,
      sku: item.sku,
      name: item.name,
      productDescription: item.productDescription,
      unitPrice: [item.unitPrice, [Validators.required]],
      baseUnitPrice: baseUnitPrice,
      baseQuantity: baseQuantity,
      quantity: [
        0,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(~~Number(maxQuantity)),
        ],
      ],
      availableQuantity: parseFloat(Number(maxQuantity).toFixed(2)),
      orderedQuantity: maxBaseQuantity,
      quantityOrdered: item.orderedQuantity,
      quantityCancelled: item.quantityCancelled,
      quantityShipped: item.quantityShipped,
      quantityReturned: item?.totalReturnedBaseQuantity,
      lotId: [
        item.lotId,
        [this.initFieldValidationlot(this.isRestock, hasLotTracking)],
      ],
      selectedLot: {
        value: null,
        label: '',
        subLabel: '',
      },
      locationId: [
        item.locationId,
        [this.initFieldValidationLocation(this.isRestock)],
      ],
      selectedLocation: {
        value: null,
        label: '',
        subLabel: '',
      },
      uomId: [
        itemType === EItemType.PRODUCT ? item?.orderedUomId : null,
        [this.initFieldValidationUom(itemType)],
      ],
      selectedUnitOfMeasure: {
        value: itemType === EItemType.PRODUCT ? item?.orderedUom?.id : null,
        label: itemType === EItemType.PRODUCT ? item?.orderedUom?.name : '',
        subLabel: '',
      },
      baseUomId: item.baseUomId,
      selectedBaseUnitOfMeasure: {
        value: item?.baseUom?.id,
        label: item?.baseUom?.name,
        subLabel: '',
      },
      unitClassId: item?.rfq?.unitClassId || 0,
      itemType: itemType,
      hasLotTracking: hasLotTracking,
    });
  }

  private getReturnOrderIsoDate(returnOrderDate ?:any) :string {
    const date = returnOrderDate ? new Date(returnOrderDate) : new Date();
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    const isoDate = `${year}-${month}-${day}`;

    return isoDate;
  }

  private initForm() :void {
    this.form = this.fb.group({
      orderReturnDate: [null, [Validators.required]],
      restock: [false, [Validators.required]],
      customRefundAmount: [0, [Validators.required]],
      restockingFee: [0, [Validators.required]],
      orderReturnItems: this.fb.array([]),
    });

    if (this.currentOrderReturn && this.currentOrderReturn.id) {
      if (this.currentOrderReturn && this.currentOrderReturn.orderDate) {
        const returnOrderDate = this.getReturnOrderIsoDate(
          this.currentOrderReturn?.orderDate,
        );
        this.form?.get('orderReturnDate')?.setValue(returnOrderDate);
      }

      const orderReturnItems = this.currentOrderReturnItems;

      const restockingFee = orderReturnItems.find(
        (orderReturnItem :OrderReturnItemInterface) =>
          orderReturnItem?.rfq?.internalSku ===
          OrderReturnCustomItems.RESTOCKING_FEE,
      );

      const customRefundAmount = orderReturnItems.find(
        (orderReturnItem :OrderReturnItemInterface) =>
          orderReturnItem?.rfq?.internalSku ===
          OrderReturnCustomItems.CUSTOM_REFUND_AMOUNT,
      );

      this.form?.get('restockingFee')?.setValue(restockingFee?.unitPrice || 0);
      this.form
        ?.get('customRefundAmount')
        ?.setValue(customRefundAmount?.unitPrice || 0);

      const customItems = [
        OrderReturnCustomItems.CUSTOM_REFUND_AMOUNT as string,
        OrderReturnCustomItems.RESTOCKING_FEE as string,
      ];

      this.currentOrderReturnItems = orderReturnItems.filter(
        (orderReturnItem :OrderReturnItemInterface) => {
          return !customItems.includes(orderReturnItem?.rfq?.internalSku || '');
        },
      );

      this.form
        ?.get('restock')
        ?.setValue(this.currentOrderReturn?.skipInventory === false ? true : false);
    }

    let formattedItems = [];
    if (this.currentOrderReturnItems?.length) {
      formattedItems = this.currentOrderReturnItems.map(
        (item :OrderReturnItemInterface) =>
          this.createItemFromExistingOrderReturn(item),
      );

      formattedItems.forEach((item) => {
        this.items.push(item);
      });
    } else {
      const returnOrderDate = this.getReturnOrderIsoDate();
      this.form?.get('orderReturnDate')?.setValue(returnOrderDate);

      if (this.currentParentSalesOrderItems?.length) {
        formattedItems = this.currentParentSalesOrderItems.map(
          (item :OrderSalesItemInterface) =>
            this.createItemFromParentSalesOrder(item),
        );

        formattedItems.forEach((item) => {
          this.items.push(item);
        });
      }
    }
  }

  private loadOrderReturn(orderReturnId :number) :void {
    this.orderReturnService.getOrderReturnById({
      orderReturnId,
      params: {
        relations:
          'orderItems.rfq,orderItems.kit,orderItems.orderedUom,orderItems.baseUom,orderItems.rfqStockLog.location,orderItems.rfqStockLog.rfqLot',
      },
    });
  }

  private loadParentSalesOrder(parentSalesOrderId :number) :void {
    this.orderReturnService.getParentSalesOrderById({
      parentSalesOrderId,
      params: {
        relations:
          'orderItems.rfq,orderItems.kit,orderItems.orderedUom,orderItems.baseUom',
      },
    });
  }

  private getDataFromService() :void {
    this.currentParentSalesOrder$ =
      this.orderReturnService.currentParentSalesOrder$;
    this.currentOrderReturn$ = this.orderReturnService.currentOrderReturn$;
    this.loading$ = this.orderReturnService.loading$;
    this.loadingLocations$ = this.locationApiService.loading$;
    this.loadingLots$ = this.lotApiService.loading$;
    this.loadingUnitOfMeasures$ = this.unitOfMeasureApiService.loading$;
    this.locationsByRfqId$ = this.locationApiService.locationsByRfqId$;
    this.lotsByRfqId$ = this.lotApiService.lotsByRfqId$;
    this.unitOfMeasuresByRfqId$ =
      this.unitOfMeasureApiService.unitOfMeasuresByRfqId$;
    this.submittingForm$ = this.orderReturnService.submittingForm$;
  }

  private getParentSalesOrder() :Observable<OrderSalesInterface> {
    return this.currentParentSalesOrder$.pipe(
      tap((currentParentSalesOrder) => {
        if (currentParentSalesOrder) {
          this.currentParentSalesOrder = currentParentSalesOrder;
          this.currentParentSalesOrderItems = currentParentSalesOrder.orderItems;
        }
        this.initForm();
        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getParentSalesOrderAndOrderReturn() :Observable<any> {
    return zip(this.currentParentSalesOrder$, this.currentOrderReturn$).pipe(
      tap(([currentParentSalesOrder, currentOrderReturn] :any) => {
        if (currentParentSalesOrder && this.parentSalesOrderId) {
          this.currentParentSalesOrder = currentParentSalesOrder;
          this.currentParentSalesOrderItems = currentParentSalesOrder?.orderItems;
        }

        if (currentOrderReturn && this.orderReturnId) {
          this.currentOrderReturn = currentOrderReturn;
          this.currentOrderReturnItems = currentOrderReturn?.orderItems;
        }

        this.initForm();
        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { parentSalesOrderId, orderReturnId } = params;
        if (parentSalesOrderId) {
          this.parentSalesOrderId = parentSalesOrderId;
          this.loadParentSalesOrder(this.parentSalesOrderId);
        }
        if (orderReturnId) {
          this.orderReturnId = orderReturnId;
          this.loadOrderReturn(this.orderReturnId);
        }
        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getLocationsById() :Observable<{
    [key :string] :ILocation[];
  }> {
    return this.locationsByRfqId$.pipe(
      tap((locations) => {
        this.locationsByRfqId = locations;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getLotsById() :Observable<{
    [rfqId :string] :{ [locationId :string] :any[] };
  }> {
    return this.lotsByRfqId$.pipe(
      tap((lots) => {
        this.lotsByRfqId = lots;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getUnitOfMeasuresById() :Observable<{
    [key :string] :IUnitOfMeasure[];
  }> {
    return this.unitOfMeasuresByRfqId$.pipe(
      tap((unitOfMeasures) => {
        this.unitOfMeasuresByRfqId = unitOfMeasures;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getMatchedItemFromOrderReturn(
    item :OrderReturnItemInterface,
  ) :OrderSalesItemInterface | undefined {
    if (this.currentParentSalesOrderItems?.length) {
      return this.currentParentSalesOrderItems.find((parentSalesOrderItem) => {
        if (
          parentSalesOrderItem.rfqId === item.rfqId ||
          parentSalesOrderItem.kitId === item.kitId ||
          parentSalesOrderItem.productId === item.productId
        ) {
          return true;
        }

        return false;
      });
    }

    return undefined;
  }

  private initFieldValidationLocation(
    isRestock :boolean,
    quantity :number = 0,
  ) :Validators {
    return (control :UntypedFormControl) => {
      return isRestock && quantity > 0 ? Validators.required(control) : null;
    };
  }

  private initFieldValidationlot(
    isRestock :boolean,
    hasLotTracking :boolean,
    quantity :number = 0,
  ) :Validators {
    return (control :UntypedFormControl) => {
      return isRestock && hasLotTracking && quantity > 0
        ? Validators.required(control)
        : null;
    };
  }

  private initFieldValidationUom(itemType :string) :Validators {
    return (control :UntypedFormControl) => {
      return itemType === EItemType.PRODUCT ? Validators.required(control) : null;
    };
  }

  private setValidationsLotAndLocation(index :number) :void {
    const item = this.items.controls[index];

    item
      .get('lotId')
      ?.setValidators(
        this.isRestock &&
          item.get('hasLotTracking')?.value &&
          item.get('quantity')?.value > 0
          ? [Validators.required]
          : [],
      );
    item
      .get('locationId')
      ?.setValidators(
        this.isRestock && item.get('quantity')?.value > 0
          ? [Validators.required]
          : [],
      );

    item.get('lotId')?.updateValueAndValidity({
      onlySelf: true,
    });
    item.get('locationId')?.updateValueAndValidity({
      onlySelf: true,
    });
    this.cd.detectChanges();
  }

  private setValidationsQuantity(index :number) :void {
    const item = this.items.controls[index];

    item
      .get('quantity')
      ?.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(~~item.get('availableQuantity')?.value),
      ]);

    item.get('quantity')?.updateValueAndValidity({
      onlySelf: true,
    });

    this.cd.detectChanges();
  }

  private rowResetLotAndLocation(item :AbstractControl) :void {
    item.patchValue({
      locationId: null,
      lotId: null,
      selectedLocation: {
        value: null,
        label: '',
        subLabel: '',
      },
      selectedLot: {
        value: null,
        label: '',
        subLabel: '',
      },
    });
    this.cd.detectChanges();
  }

  get formattedLocations() :OptionInterface[] {
    if (
      this.locationsByRfqId &&
      this.currentRfqId &&
      this.locationsByRfqId[this.currentRfqId]?.length
    ) {
      return this.locationsByRfqId[this.currentRfqId]?.map((location) => {
        return {
          value: location.id,
          label: `${location.locationName} - (${location?.parent?.locationName})`,
          subLabel: '',
        };
      });
    } else {
      return [];
    }
  }

  get formattedLots() :OptionInterface[] {
    if (
      this.lotsByRfqId &&
      this.currentRfqId &&
      this.currentLocationId &&
      this.lotsByRfqId[this.currentRfqId] &&
      this.lotsByRfqId[this.currentRfqId][this.currentLocationId]
    ) {
      if (this.lotsByRfqId[this.currentRfqId][this.currentLocationId]?.length) {
        return this.lotsByRfqId[this.currentRfqId][this.currentLocationId]?.map(
          (lot) => {
            return {
              value: lot?.id,
              label: lot?.lotNumber,
              subLabel: lot?.productionDate,
            };
          },
        );
      } else {
        return [];
      }
    } else {
      return [];
    }
  }

  get formattedUnitOfMeasures() :OptionInterface[] {
    if (
      this.unitOfMeasuresByRfqId &&
      this.currentRfqId &&
      this.unitOfMeasuresByRfqId[this.currentRfqId]?.length
    ) {
      return this.unitOfMeasuresByRfqId[this.currentRfqId]?.map((unitOfMeasure) => {
        return {
          value: unitOfMeasure.id,
          label: unitOfMeasure.name,
          subLabel: '',
        };
      });
    } else {
      return [];
    }
  }

  get items() :UntypedFormArray {
    return this.form.get('orderReturnItems') as UntypedFormArray;
  }

  get orderTotal() :number {
    return this.items.controls.reduce(
      (acc, item) =>
        acc +
        Number(item.value.quantity) *
          Number(item.value.baseUnitPrice) *
          Number(item.value.baseQuantity),
      0,
    );
  }

  get itemSubtotalRefund() :number {
    return this.items.controls.reduce(
      (acc, item) =>
        acc +
        Number(item.value.quantity) *
          Number(item.value.baseUnitPrice) *
          Number(item.value.baseQuantity),
      0,
    );
  }

  get refundItemQuantity() :number {
    return this.items.controls.reduce(
      (acc, item) => acc + Number(item.value.quantity),
      0,
    );
  }

  get grandTotalRefund() :number {
    return (
      Number(this.itemSubtotalRefund) +
        Number(this.restockingFee) +
        Number(this.customRefundAmount) ?? 0
    );
  }

  get customRefundAmount() :number {
    return Number(this.form.value?.customRefundAmount) ?? 0;
  }

  get restockingFee() :number {
    return Number(this.form.value?.restockingFee) ?? 0;
  }

  get isRestock() :boolean {
    return this.form.value?.restock ?? false;
  }
}
